import { ChevronRight } from '@material-ui/icons';
import { BaseClickable, Box, Link, palette, Stack, style, Text } from '@solvhealth/jigsaw-v1';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import moment from 'moment';
import useInPersonPlusPricing, {
  useInPersonPlusPriceRangeStr,
} from '~/components/Membership/hooks/useInPersonPlusPricing';
import {
  PREMIUM_IN_PERSON_BOOKING_ENTRYPOINT_ACCOUNT_HOME,
  PREMIUM_IN_PERSON_START_BOOKING_FLOW,
} from '~/core/analytics/events';
import useToggleRuntimeModal from '~/hooks/runtime/useToggleRuntimeModal';
import {
  PREMIUM_IN_PERSON_SLOT_SELECTOR,
  PremiumInPersonSlotSelector,
} from './PremiumInPersonSlotSelector';
import {
  getProfileFromAccountSummary,
  isUserProfileCoveredByMembership,
} from '~/core/util/account';
import { useAccount } from '~/hooks/account/useAccount';
import { getImageObject } from '~/components/SolvPatternLibrary/Picture/util';
import { useMostConvenientInPersonPremiumLocation } from '~/components/AccountGetsMarried/components/GetCare/components/NavTiles/hooks/useMostConvenientInPersonPremiumLocation';

const StyledBox = styled(Box).withConfig({ componentId: 'sc-4xhk0e-0' })`
  display: flex;
  flex-wrap: wrap;
`;

const LocationImage = styled.img.withConfig({ componentId: 'sc-4xhk0e-1' })(
  style({
    borderRadius: 'full',
    overflow: 'hidden',
    objectFit: 'fill',
    flexShrink: 0,
  })
);

type Props = {
  userProfileId: string;
};

const PremiumInPersonEntrypoint = ({ userProfileId }: Props) => {
  const { t } = useTranslation(['account', 'common']);
  const [, toggle] = useToggleRuntimeModal(PREMIUM_IN_PERSON_SLOT_SELECTOR);
  const priceTiers = useInPersonPlusPricing();
  const priceRangeStr = useInPersonPlusPriceRangeStr();

  const { mostConvenientLocation, availableDay, availableSlot } =
    useMostConvenientInPersonPremiumLocation();

  const userProfile = getProfileFromAccountSummary(useAccount(), userProfileId);
  const isUserProfileWithMembershipAccess = isUserProfileCoveredByMembership(
    useAccount(),
    userProfile
  );

  const getBookNowCta = () => {
    if (availableDay === 'today') {
      return t('account:availability.today', {
        date: moment(availableSlot),
      });
    }
    if (availableDay === 'tomorrow') {
      return t('account:availability.tomorrow', {
        date: moment(availableSlot),
      });
    }
    return t('account:inPersonPremium.accountCard.genericCta');
  };

  const image = mostConvenientLocation
    ? getImageObject(mostConvenientLocation, {
        imageIndex: 0,
        showDefaultPictureIfMissing: true,
      })
    : null;

  return (
    <Stack borderRadius="base" boxShadow="xs" flexShrink={0}>
      <BaseClickable
        event={PREMIUM_IN_PERSON_START_BOOKING_FLOW}
        meta={{
          entrypointId: PREMIUM_IN_PERSON_BOOKING_ENTRYPOINT_ACCOUNT_HOME,
          availableDay,
          priceRangeLow: priceTiers.low,
          priceRangeHigh: priceTiers.high,
          priceRangeString: `${priceTiers.low}-${priceTiers.high}`,
        }}
        onClick={toggle}
      >
        <Stack alignItems="flex-start" p={2} space={2}>
          <Stack row>
            <Stack space={0.5}>
              {mostConvenientLocation ? (
                <Text variant="h4">
                  {mostConvenientLocation?.displayNamePrimary} -{' '}
                  {mostConvenientLocation?.displayNameSecondary}
                </Text>
              ) : (
                <Stack alignItems="center" row space={1}>
                  <Box
                    alignItems="center"
                    bg="teal"
                    borderRadius="sm"
                    display="inline"
                    fontSize="xs"
                    fontWeight="bold"
                    sx={{ textTransform: 'uppercase', pt: '2px', pb: '2px', px: 0.5 }}
                    textColor="white"
                  >
                    {t('common:new')}
                  </Box>
                  <Text variant="h4">{t('account:inPersonPremium.accountCard.title')}</Text>
                </Stack>
              )}

              <Text muted>{t('account:inPersonPremium.accountCard.subtitle')}</Text>
            </Stack>
            {image?.fullRes && <LocationImage height={44} src={image.fullRes} width={44} />}
          </Stack>
          <Stack
            alignItems="center"
            flexWrap="wrap"
            justifyContent="space-between"
            row
            width="100%"
          >
            <Stack alignItems="center" row space={0.5}>
              <Text textColor="crunchberry">{getBookNowCta()}</Text>
              <ChevronRight htmlColor={palette.crunchberry.base} style={{ fontSize: '18px' }} />
            </Stack>
            <Stack alignItems="center" row space={1}>
              <Text textColor="teal-medium" variant="subtitle">
                {priceRangeStr}
              </Text>
            </Stack>
          </Stack>
        </Stack>
      </BaseClickable>
      {!isUserProfileWithMembershipAccess && (
        <StyledBox bg="teal-xlight" px={2} py={1.5}>
          <Stack alignItems="center" flexWrap="wrap" pr={1} row space={1}>
            <Text as="span">Get ClearPrice with</Text>
            <img src="/images/membership/logo_full.svg" width={72} />
          </Stack>
          <Link
            href="/membership/in-person"
            inline
            subtle
            sx={{ textDecoration: 'underline', textAlign: 'left' }}
          >
            {t('account:solvPremiumEntrypoint.linkCta')}
          </Link>
        </StyledBox>
      )}
      <PremiumInPersonSlotSelector />
    </Stack>
  );
};

export default PremiumInPersonEntrypoint;
